import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import FontFamily from '../../constants/fonts';
import { AdminColors } from '../Admin/constants/adminColors';
import { useTheme } from '../../theme';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

interface UnsavedChangesDialogProps {
  visible: boolean;
  /** 'save' = normal unsaved-changes flow; 'missing-fields' = required fields absent, no save option */
  mode?: 'save' | 'missing-fields';
  onSaveForLater: () => Promise<void>;
  onContinueEditing: () => void;
  onCloseWithoutSaving?: () => void;
  isLoading?: boolean;
}

const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  visible,
  mode = 'save',
  onSaveForLater,
  onContinueEditing,
  onCloseWithoutSaving,
  isLoading = false,
}) => {
  const { t } = useTranslation('perfectTruck');
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSaveForLater();
    } finally {
      setIsSaving(false);
    }
  };

  const isMissingFields = mode === 'missing-fields';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {}}
    >
      <View style={styles.overlay}>
        <View style={[styles.dialog, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1 }]}>
          <View style={styles.header}>
            <MaterialIcons name="warning" size={24} color={dc(AdminColors.yellow200)} />
            <Text style={[styles.title, isDark && { color: colors.text.primary }]}>
              {isMissingFields ? t('unsaved_dialog.title_missing_fields') : t('unsaved_dialog.title_unsaved')}
            </Text>
          </View>

          <Text style={[styles.message, isDark && { color: colors.text.secondary }]}>
            {isMissingFields
              ? t('unsaved_dialog.message_missing_fields')
              : t('unsaved_dialog.message_unsaved')}
          </Text>

          <View style={styles.actions}>
            <TouchableOpacity
              style={[styles.button, styles.buttonSecondary, isDark && { borderColor: colors.border.default, backgroundColor: colors.background.elevated }]}
              onPress={onContinueEditing}
              disabled={isSaving}
            >
              <Text style={[styles.buttonText, styles.buttonTextSecondary, isDark && { color: colors.text.primary }]}>
                {t('unsaved_dialog.btn_continue_editing')}
              </Text>
            </TouchableOpacity>

            {isMissingFields ? (
              <TouchableOpacity
                style={[styles.button, styles.buttonDanger]}
                onPress={onCloseWithoutSaving}
                disabled={isSaving}
              >
                <Text style={[styles.buttonText, styles.buttonTextPrimary]}>
                  {t('unsaved_dialog.btn_close_without_saving')}
                </Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[styles.button, styles.buttonPrimary]}
                onPress={handleSave}
                disabled={isSaving}
              >
                <Text style={[styles.buttonText, styles.buttonTextPrimary]}>
                  {isSaving ? t('unsaved_dialog.btn_saving') : t('unsaved_dialog.btn_save_for_later')}
                </Text>
              </TouchableOpacity>
            )}
          </View>
          {!isMissingFields && onCloseWithoutSaving && (
            <TouchableOpacity
              style={styles.discardLink}
              onPress={onCloseWithoutSaving}
              disabled={isSaving}
            >
              <Text style={[styles.discardLinkText, isDark && { color: colors.text.secondary }]}>{t('unsaved_dialog.link_leave_without_saving')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );
};

const baseStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialog: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 12,
    padding: 24,
    maxWidth: 400,
    ...Platform.select({
      web: { boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)' } as any,
      default: { elevation: 8 },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  title: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    color: AdminColors.textPrimary,
  },
  message: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textMuted,
    lineHeight: 20,
    marginBottom: 24,
  },
  actions: {
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonSecondary: {
    borderWidth: 1.5,
    borderColor: AdminColors.borderDefault,
    backgroundColor: AdminColors.bgSurface,
  },
  buttonPrimary: {
    backgroundColor: AdminColors.blue700,
  },
  buttonDanger: {
    backgroundColor: AdminColors.red400,
  },
  buttonText: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
  },
  buttonTextSecondary: {
    color: AdminColors.textSecondary,
  },
  buttonTextPrimary: {
    color: AdminColors.textOnDark,
  },
  discardLink: {
    alignItems: 'center',
    paddingTop: 12,
  },
  discardLinkText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
    color: AdminColors.textMuted,
    textDecorationLine: 'underline',
  },
});

export default UnsavedChangesDialog;
